import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT, useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { BookOpen, Database, FileText, Ban, Plus, Trash2, RefreshCw, Loader2, X, Zap, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { PieChart } from "@/components/PieChart";

export default function KnowledgeBase() {
  const t = useT();
  const { lang } = useLang();
  const [tab, setTab] = useState("patterns"); // patterns | bases | features | blocked
  const [patterns, setPatterns] = useState([]);
  const [totalCards, setTotalCards] = useState(0);
  const [bases, setBases] = useState([]);
  const [features, setFeatures] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [showBaseForm, setShowBaseForm] = useState(false);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [showBlockedForm, setShowBlockedForm] = useState(false);
  const [building, setBuilding] = useState(null);

  const [patternFilter, setPatternFilter] = useState({ type: "", board: "", q: "" });
  const [baseForm, setBaseForm] = useState({ name: "", description: "", filters: { period_days: 90, problem_types: "" } });
  const [featureForm, setFeatureForm] = useState({ title: "", description: "", squad_id: "" });
  const [blockedForm, setBlockedForm] = useState({ pattern_text: "", reason: "" });

  const reload = async () => {
    const [p, b, f, bl] = await Promise.all([
      api.get("/knowledge/patterns"),
      api.get("/knowledge/knowledge-bases"),
      api.get("/knowledge/feature-specs"),
      api.get("/knowledge/blocked-patterns"),
    ]);
    setPatterns(p.data.patterns || []);
    setTotalCards(p.data.total_cards_analyzed || 0);
    setBases(b.data.knowledge_bases || []);
    setFeatures(f.data.feature_specs || []);
    setBlocked(bl.data.blocked_patterns || []);
  };
  useEffect(() => { reload(); }, []);

  const blockPattern = async (id) => {
    if (!confirm(lang === "pt" ? "Bloquear este padrão? Não será gerado novamente." : "Block this pattern? Won't be generated again.")) return;
    await api.post(`/knowledge/patterns/${id}/block`);
    toast.success(lang === "pt" ? "Padrão bloqueado" : "Pattern blocked");
    reload();
  };

  const deletePattern = async (id) => {
    if (!confirm(lang === "pt" ? "Excluir definitivamente?" : "Delete permanently?")) return;
    await api.delete(`/knowledge/patterns/${id}`);
    reload();
  };

  const createBase = async (e) => {
    e.preventDefault();
    const filters = {
      period_days: parseInt(baseForm.filters.period_days) || 90,
      problem_types: (baseForm.filters.problem_types || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    await api.post("/knowledge/knowledge-bases", { ...baseForm, filters });
    toast.success("KB created");
    setShowBaseForm(false);
    setBaseForm({ name: "", description: "", filters: { period_days: 90, problem_types: "" } });
    reload();
  };

  const rebuildBase = async (id) => {
    setBuilding(id);
    try {
      const { data } = await api.post(`/knowledge/knowledge-bases/${id}/rebuild`);
      toast.success(`${data.pattern_count} ${lang === "pt" ? "padrões agregados de" : "patterns aggregated from"} ${data.cards_scanned} cards`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBuilding(null); }
  };

  const deleteBase = async (id) => {
    if (!confirm(lang === "pt" ? "Excluir esta KB?" : "Delete this KB?")) return;
    await api.delete(`/knowledge/knowledge-bases/${id}`);
    reload();
  };

  const createFeature = async (e) => {
    e.preventDefault();
    await api.post("/knowledge/feature-specs", featureForm);
    toast.success("Feature spec created");
    setShowFeatureForm(false);
    setFeatureForm({ title: "", description: "", squad_id: "" });
    reload();
  };

  const deleteFeature = async (id) => {
    if (!confirm(lang === "pt" ? "Excluir?" : "Delete?")) return;
    await api.delete(`/knowledge/feature-specs/${id}`);
    reload();
  };

  const createBlocked = async (e) => {
    e.preventDefault();
    await api.post("/knowledge/blocked-patterns", blockedForm);
    setShowBlockedForm(false);
    setBlockedForm({ pattern_text: "", reason: "" });
    reload();
  };

  const unblock = async (id) => {
    await api.delete(`/knowledge/blocked-patterns/${id}`);
    reload();
  };

  const tabs = [
    { key: "patterns", label: lang === "pt" ? "Padrões" : "Patterns", Icon: Database, count: patterns.length },
    { key: "bases", label: lang === "pt" ? "Bases" : "Bases", Icon: BookOpen, count: bases.length },
    { key: "features", label: lang === "pt" ? "Features" : "Features", Icon: FileText, count: features.length },
    { key: "blocked", label: lang === "pt" ? "Bloqueados" : "Blocked", Icon: Ban, count: blocked.length },
  ];

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="overline mb-3">// knowledge · patterns · features · bases</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Knowledge Base</h1>
      <p className="text-white/50 mb-8 text-sm">
        {totalCards} {lang === "pt" ? "cards processados · padrões extraídos por IA dos relatórios concluídos" : "cards processed · patterns extracted by AI from completed reports"}
      </p>

      {/* Tabs */}
      <div className="flex border-b border-white/10 mb-6">
        {tabs.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`kb-tab-${tb.key}`}
            className={`px-4 py-2.5 text-xs font-mono uppercase tracking-wider transition border-b-2 -mb-px flex items-center gap-2 ${tab === tb.key ? "text-primary border-primary" : "text-white/40 border-transparent hover:text-white/70"}`}>
            <tb.Icon className="w-3.5 h-3.5" /> {tb.label} <span className="text-white/30">({tb.count})</span>
          </button>
        ))}
      </div>

      {/* PATTERNS */}
      {tab === "patterns" && (() => {
        const types = Array.from(new Set(patterns.map((p) => p.problem_type || "unknown"))).sort();
        const allBoards = Array.from(new Set(patterns.flatMap((p) => p.sources_seen || []))).sort();
        const q = (patternFilter.q || "").toLowerCase();
        const filtered = patterns.filter((p) => {
          if (patternFilter.type && (p.problem_type || "unknown") !== patternFilter.type) return false;
          if (patternFilter.board && !(p.sources_seen || []).includes(patternFilter.board)) return false;
          if (q && !(p.pattern_text || "").toLowerCase().includes(q)) return false;
          return true;
        });
        // Aggregate stats across all patterns
        const supportByType = {};
        const cardsByType = {};
        for (const p of patterns) {
          const tp = p.problem_type || "unknown";
          supportByType[tp] = (supportByType[tp] || 0) + (p.support_count || 0);
          cardsByType[tp] = (cardsByType[tp] || 0) + ((p.examples || []).length);
        }
        const totalSupport = Object.values(supportByType).reduce((a, b) => a + b, 0) || 1;
        const pctByType = Object.fromEntries(
          Object.entries(supportByType).map(([k, v]) => [k, Math.round((v / totalSupport) * 1000) / 10])
        );
        const topType = Object.entries(supportByType).sort((a, b) => b[1] - a[1])[0];
        return (
          <>
            {/* Aggregate stats panel */}
            {patterns.length > 0 && (
              <div className="surface p-4 mb-4 grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6 items-start" data-testid="kb-patterns-aggregate">
                <div>
                  <div className="overline mb-3">{lang === "pt" ? "Panorama" : "Overview"}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="text-center p-3 bg-white/5 rounded-sm">
                      <div className="text-xl font-bold text-white">{totalCards}</div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40">{lang === "pt" ? "Cards processados" : "Cards processed"}</div>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-sm">
                      <div className="text-xl font-bold text-white">{patterns.length}</div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40">{lang === "pt" ? "Padrões ativos" : "Active patterns"}</div>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-sm">
                      <div className="text-xl font-bold text-white">{types.length}</div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40">{lang === "pt" ? "Tipos de problema" : "Problem types"}</div>
                    </div>
                    <div className="text-center p-3 bg-emerald-400/5 rounded-sm">
                      <div className="text-xs font-bold text-emerald-400 truncate" title={topType ? topType[0] : ""}>{topType ? topType[0] : "—"}</div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-400/60">{lang === "pt" ? "Tipo mais comum" : "Top type"}{topType ? ` · ${Math.round((topType[1] / totalSupport) * 100)}%` : ""}</div>
                    </div>
                  </div>
                </div>
                {Object.keys(pctByType).length > 0 && (
                  <div className="lg:border-l lg:border-white/10 lg:pl-6">
                    <div className="overline mb-3">{lang === "pt" ? "Distribuição" : "Distribution"}</div>
                    <PieChart data={pctByType} size={140} />
                  </div>
                )}
              </div>
            )}

            {/* Banner: KB now feeds the pipeline */}
            <div className="surface p-3 mb-4 flex items-center gap-3 border-emerald-400/20 bg-emerald-400/5" data-testid="kb-active-banner">
              <Zap className="w-4 h-4 text-emerald-400 shrink-0" />
              <div className="text-xs text-white/70 leading-snug">
                {lang === "pt"
                  ? "Padrões abaixo são injetados automaticamente como contexto histórico nos agentes de análise e hipótese de novas investigações. Bloqueie ou exclua os irrelevantes."
                  : "Patterns below are automatically injected as historical context into the analyzer and hypothesis agents of new investigations. Block or delete the irrelevant ones."}
              </div>
            </div>

            {/* Filters */}
            {patterns.length > 0 && (
              <div className="flex gap-2 mb-4 items-center" data-testid="kb-patterns-filter">
                <Search className="w-3.5 h-3.5 text-white/40" />
                <input
                  value={patternFilter.q}
                  onChange={(e) => setPatternFilter({ ...patternFilter, q: e.target.value })}
                  placeholder={lang === "pt" ? "buscar no texto do padrão..." : "search pattern text..."}
                  className="bg-white/5 border border-white/10 px-2 py-1 text-xs flex-1 font-mono"
                  data-testid="kb-patterns-search"
                />
                <select
                  value={patternFilter.type}
                  onChange={(e) => setPatternFilter({ ...patternFilter, type: e.target.value })}
                  className="bg-white/5 border border-white/10 px-2 py-1 text-xs font-mono"
                  data-testid="kb-patterns-type-filter"
                >
                  <option value="">{lang === "pt" ? "todos os tipos" : "all types"}</option>
                  {types.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                </select>
                {allBoards.length > 0 && (
                  <select
                    value={patternFilter.board}
                    onChange={(e) => setPatternFilter({ ...patternFilter, board: e.target.value })}
                    className="bg-white/5 border border-white/10 px-2 py-1 text-xs font-mono"
                    data-testid="kb-patterns-board-filter"
                  >
                    <option value="">{lang === "pt" ? "todos os boards" : "all boards"}</option>
                    {allBoards.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                )}
                <span className="text-[10px] font-mono text-white/30">
                  {filtered.length}/{patterns.length}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="kb-patterns-grid">
              {filtered.map((p) => (
                <div key={p.id} className="surface p-4" data-testid={`kb-pattern-${p.id}`}>
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="overline truncate" title="problem_type">{p.problem_type || "unknown"}</span>
                      <span
                        className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 rounded-sm shrink-0"
                        title={lang === "pt" ? "Padrão é injetado em novas investigações" : "Pattern is injected into new investigations"}
                      >
                        active
                      </span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => blockPattern(p.id)} className="text-white/40 hover:text-amber-400 p-1" title="Block" data-testid={`kb-pattern-block-${p.id}`}><Ban className="w-3 h-3" /></button>
                      <button onClick={() => deletePattern(p.id)} className="text-white/40 hover:text-red-400 p-1" title="Delete" data-testid={`kb-pattern-delete-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <p className="text-sm mb-3 leading-snug">{p.pattern_text}</p>
                  {(p.sources_seen || []).length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1 items-center text-[10px] font-mono">
                      <span className="text-white/30">{lang === "pt" ? "observado em" : "seen in"}:</span>
                      {(p.sources_seen || []).map((board) => {
                        const n = ((p.examples_by_source || {})[board] || []).length;
                        return (
                          <span
                            key={board}
                            className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded-sm text-white/70"
                            title={`${n} ${lang === "pt" ? "card(s)" : "card(s)"} via ${board}`}
                          >
                            {board}{n > 0 ? ` (${n})` : ""}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {(p.examples || []).length > 0 && (
                    <div className="mb-2 text-[10px] font-mono text-white/40">
                      <span className="text-white/30">{lang === "pt" ? "exemplos" : "examples"}: </span>
                      {(p.examples || []).slice(0, 5).map((cid, idx) => (
                        <span key={cid}>
                          <Link to={`/app/cards/${cid}`} className="text-primary/80 hover:text-primary hover:underline">
                            {String(cid).slice(0, 8)}
                          </Link>
                          {idx < Math.min((p.examples || []).length, 5) - 1 ? ", " : ""}
                        </span>
                      ))}
                      {(p.examples || []).length > 5 && <span> · +{(p.examples || []).length - 5}</span>}
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-white/5 text-[10px] font-mono text-white/40 gap-2 flex-wrap">
                    <span><span className="text-primary">{p.support_count}</span>x {lang === "pt" ? "observado" : "seen"} · {(p.examples || []).length} ex</span>
                    {Number(p.used_count) > 0 && (
                      <span className="text-emerald-400/80" title={p.last_used_at ? `${lang === "pt" ? "último uso" : "last used"}: ${new Date(p.last_used_at).toLocaleString()}` : ""}>
                        <span className="text-emerald-400">{p.used_count}</span>x {lang === "pt" ? "usado" : "used"}
                        {p.last_used_at && (
                          <span className="ml-1 text-white/30">· {new Date(p.last_used_at).toLocaleDateString()}</span>
                        )}
                      </span>
                    )}
                    <span>{p.last_seen_at ? new Date(p.last_seen_at).toLocaleDateString() : "—"}</span>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="md:col-span-2 surface p-12 text-center text-white/40 text-sm">
                  <Database className="w-8 h-8 mx-auto mb-3 opacity-30" />
                  {patterns.length === 0
                    ? (lang === "pt"
                        ? "Nenhum padrão ainda. Crie uma Knowledge Base e clique Rebuild para extrair dos cards concluídos."
                        : "No patterns yet. Create a Knowledge Base and click Rebuild to extract from completed cards.")
                    : (lang === "pt" ? "Nenhum padrão corresponde aos filtros." : "No patterns match the filters.")}
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* KNOWLEDGE BASES */}
      {tab === "bases" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowBaseForm(!showBaseForm)} className="btn-primary text-xs py-2 px-3" data-testid="kb-base-new-btn">
              <Plus className="w-3.5 h-3.5" /> {lang === "pt" ? "Nova KB" : "New KB"}
            </button>
          </div>
          {showBaseForm && (
            <form onSubmit={createBase} className="surface p-5 mb-6 space-y-3" data-testid="kb-base-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input required value={baseForm.name} onChange={(e) => setBaseForm({ ...baseForm, name: e.target.value })}
                  placeholder={lang === "pt" ? "Nome (ex: Pagamentos · últimos 90d)" : "Name (e.g. Payments · last 90d)"}
                  className="bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="kb-base-name" />
                <input value={baseForm.description} onChange={(e) => setBaseForm({ ...baseForm, description: e.target.value })}
                  placeholder={lang === "pt" ? "Descrição" : "Description"}
                  className="bg-white/5 border border-white/10 px-3 py-2 text-sm" />
                <input type="number" value={baseForm.filters.period_days} onChange={(e) => setBaseForm({ ...baseForm, filters: { ...baseForm.filters, period_days: e.target.value } })}
                  placeholder="period_days" className="bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" />
                <input value={baseForm.filters.problem_types} onChange={(e) => setBaseForm({ ...baseForm, filters: { ...baseForm.filters, problem_types: e.target.value } })}
                  placeholder="problem_types (csv)" className="bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" />
              </div>
              <div className="flex gap-2">
                <button className="btn-primary text-sm" data-testid="kb-base-save">{lang === "pt" ? "Criar" : "Create"}</button>
                <button type="button" onClick={() => setShowBaseForm(false)} className="btn-ghost text-sm">{lang === "pt" ? "Cancelar" : "Cancel"}</button>
              </div>
            </form>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bases.map((b) => (
              <div key={b.id} className="surface p-4" data-testid={`kb-base-${b.id}`}>
                <div className="flex justify-between items-start mb-2 gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold">{b.name}</div>
                    <div className="text-xs text-white/50">{b.description}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => rebuildBase(b.id)} disabled={building === b.id} className="text-white/60 hover:text-primary p-1" title="Rebuild" data-testid={`kb-base-rebuild-${b.id}`}>
                      {building === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => deleteBase(b.id)} className="text-white/40 hover:text-red-400 p-1" data-testid={`kb-base-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="font-mono text-[10px] text-white/40 mt-2">
                  filters: {JSON.stringify(b.filters || {})}
                </div>
                <div className="font-mono text-[10px] text-white/40">
                  patterns: {b.pattern_count ?? 0} · last_built: {b.last_built_at ? new Date(b.last_built_at).toLocaleString() : "never"}
                </div>
                {b.last_result && (
                  <div className="mt-4 pt-3 border-t border-white/10 space-y-3" data-testid={`kb-base-stats-${b.id}`}>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center p-2 bg-white/5 rounded-sm">
                        <div className="text-base font-bold text-white">{b.last_result.total_cards ?? 0}</div>
                        <div className="text-[9px] uppercase tracking-wider text-white/40">{lang === "pt" ? "Cards" : "Cards"}</div>
                      </div>
                      <div className="text-center p-2 bg-white/5 rounded-sm">
                        <div className="text-base font-bold text-white">{b.last_result.total_processed ?? 0}</div>
                        <div className="text-[9px] uppercase tracking-wider text-white/40">{lang === "pt" ? "Processados" : "Processed"}</div>
                      </div>
                      <div className="text-center p-2 bg-emerald-400/5 rounded-sm">
                        <div className="text-base font-bold text-emerald-400">{b.last_result.patterns_count ?? 0}</div>
                        <div className="text-[9px] uppercase tracking-wider text-emerald-400/60">{lang === "pt" ? "Padrões" : "Patterns"}</div>
                      </div>
                    </div>
                    {b.last_result.percentages && Object.keys(b.last_result.percentages).length > 0 && (
                      <div data-testid={`kb-base-piechart-${b.id}`}>
                        <div className="overline mb-3">{lang === "pt" ? "Distribuição por tipo" : "Distribution by type"}</div>
                        <PieChart data={b.last_result.percentages} size={140} />
                      </div>
                    )}
                    {b.last_result.panorama_md && (
                      <div data-testid={`kb-base-panorama-${b.id}`}>
                        <div className="overline mb-2">{lang === "pt" ? "Panorama IA" : "AI panorama"}</div>
                        <pre className="text-[11px] text-white/70 whitespace-pre-wrap font-mono leading-relaxed bg-black/20 p-3 rounded-sm border border-white/5 max-h-72 overflow-auto">
{b.last_result.panorama_md}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {bases.length === 0 && <div className="md:col-span-2 surface p-12 text-center text-white/40 text-sm">{lang === "pt" ? "Nenhuma KB ainda" : "No KB yet"}</div>}
          </div>
        </div>
      )}

      {/* FEATURE SPECS */}
      {tab === "features" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowFeatureForm(!showFeatureForm)} className="btn-primary text-xs py-2 px-3" data-testid="kb-feature-new-btn">
              <Plus className="w-3.5 h-3.5" /> {lang === "pt" ? "Nova spec" : "New spec"}
            </button>
          </div>
          {showFeatureForm && (
            <form onSubmit={createFeature} className="surface p-5 mb-6 space-y-3" data-testid="kb-feature-form">
              <input required value={featureForm.title} onChange={(e) => setFeatureForm({ ...featureForm, title: e.target.value })}
                placeholder={lang === "pt" ? "Título" : "Title"}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" data-testid="kb-feature-title" />
              <textarea required value={featureForm.description} onChange={(e) => setFeatureForm({ ...featureForm, description: e.target.value })}
                placeholder={lang === "pt" ? "Descrição da feature" : "Feature description"} rows={3}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button className="btn-primary text-sm" data-testid="kb-feature-save">{lang === "pt" ? "Criar" : "Create"}</button>
                <button type="button" onClick={() => setShowFeatureForm(false)} className="btn-ghost text-sm">{lang === "pt" ? "Cancelar" : "Cancel"}</button>
              </div>
            </form>
          )}
          <div className="space-y-3">
            {features.map((f) => (
              <div key={f.id} className="surface p-4 flex justify-between items-start gap-2" data-testid={`kb-feature-${f.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold mb-1">{f.title}</div>
                  <div className="text-sm text-white/70 leading-snug">{f.description}</div>
                  <div className="font-mono text-[10px] text-white/40 mt-2">{(f.derived_from_card_ids || []).length} cards · {f.source}</div>
                </div>
                <button onClick={() => deleteFeature(f.id)} className="text-white/40 hover:text-red-400 p-1" data-testid={`kb-feature-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {features.length === 0 && <div className="surface p-12 text-center text-white/40 text-sm">{lang === "pt" ? "Nenhuma feature spec ainda" : "No feature specs yet"}</div>}
          </div>
        </div>
      )}

      {/* BLOCKED */}
      {tab === "blocked" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowBlockedForm(!showBlockedForm)} className="btn-primary text-xs py-2 px-3" data-testid="kb-blocked-new-btn">
              <Plus className="w-3.5 h-3.5" /> {lang === "pt" ? "Bloquear texto" : "Block text"}
            </button>
          </div>
          {showBlockedForm && (
            <form onSubmit={createBlocked} className="surface p-5 mb-6 space-y-3" data-testid="kb-blocked-form">
              <input required value={blockedForm.pattern_text} onChange={(e) => setBlockedForm({ ...blockedForm, pattern_text: e.target.value })}
                placeholder={lang === "pt" ? "Texto/padrão a bloquear" : "Pattern text to block"}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono" data-testid="kb-blocked-text" />
              <input value={blockedForm.reason} onChange={(e) => setBlockedForm({ ...blockedForm, reason: e.target.value })}
                placeholder={lang === "pt" ? "Motivo (opcional)" : "Reason (optional)"}
                className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button className="btn-primary text-sm" data-testid="kb-blocked-save">{lang === "pt" ? "Bloquear" : "Block"}</button>
                <button type="button" onClick={() => setShowBlockedForm(false)} className="btn-ghost text-sm">{lang === "pt" ? "Cancelar" : "Cancel"}</button>
              </div>
            </form>
          )}
          <div className="space-y-2">
            {blocked.map((b) => (
              <div key={b.id} className="surface p-3 flex justify-between items-center gap-2" data-testid={`kb-blocked-${b.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs truncate">{b.pattern_text}</div>
                  <div className="font-mono text-[10px] text-white/40">{b.reason} · {b.blocked_at ? new Date(b.blocked_at).toLocaleDateString() : ""}</div>
                </div>
                <button onClick={() => unblock(b.id)} className="text-white/40 hover:text-emerald-400 p-1" title={lang === "pt" ? "Desbloquear" : "Unblock"} data-testid={`kb-blocked-unblock-${b.id}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {blocked.length === 0 && <div className="surface p-12 text-center text-white/40 text-sm">{lang === "pt" ? "Nenhum padrão bloqueado" : "No blocked patterns"}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
